import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { debounce } from 'lodash';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TRADEBOOK } from '../../../../utilities/functions';

/**
 * Central state management hook for the strategy editor.
 * Handles:
 * - Content state for current section
 * - Debounced auto-save with proper cleanup
 * - Flush pending saves before section switch
 * - Coordination between breakdown sections and global notes
 */
export default function useStrategyEditor({
    selectedPlanId,
    selectedSection,
    breakdownData,
    globalData
}) {
    const queryClient = useQueryClient();

    // Current editor content
    const [content, setContent] = useState('');

    // Track if content has been modified since last save
    const isDirtyRef = useRef(false);

    // Track what section/plan the current content belongs to
    const contentSourceRef = useRef({ planId: null, section: null });

    // Pending save data (for flushing before switch)
    const pendingSaveRef = useRef(null);

    // --- Mutations ---
    const updateBreakdownMutation = useMutation({
        mutationFn: ({ planId, updates }) => TRADEBOOK.updateBreakdown(planId, updates),
        onMutate: async ({ planId, updates }) => {
            await queryClient.cancelQueries(['tradebook-breakdown', planId]);

            const previousBreakdown = queryClient.getQueryData(['tradebook-breakdown', planId]);

            queryClient.setQueryData(['tradebook-breakdown', planId], updates);

            return { previousBreakdown };
        },
        onSuccess: () => {},
        onError: (error, variables, context) => {
            if (context?.previousBreakdown) {
                queryClient.setQueryData(
                    ['tradebook-breakdown', variables.planId],
                    context.previousBreakdown
                );
            }

            message.error('Failed to save section changes');
        }
    });

    const updateGlobalMutation = useMutation({
        mutationFn: (updates) => TRADEBOOK.updateGlobal(updates),
        onMutate: async (updates) => {
            await queryClient.cancelQueries(['tradebook-global']);

            const previousGlobal = queryClient.getQueryData(['tradebook-global']);

            queryClient.setQueryData(['tradebook-global'], updates);

            return { previousGlobal };
        },
        onSuccess: () => {},
        onError: (error, variables, context) => {
            // Rollback to previous value on error
            if (context?.previousGlobal) {
                queryClient.setQueryData(['tradebook-global'], context.previousGlobal);
            }

            message.error('Failed to save global notes');
        }
    });

    // --- Save functions ---
    const saveBreakdown = useCallback(
        (planId, section, value) => {
            if (!planId || !section || section === 'global' || !breakdownData) return;

            const [mainSection, subSection] = section.split('.');
            if (!mainSection || !subSection) return;

            // Create full object with updated value
            const fullObject = {
                ...breakdownData,
                [mainSection]: {
                    ...breakdownData[mainSection],
                    [subSection]: value
                }
            };

            updateBreakdownMutation.mutate({
                planId,
                updates: fullObject
            });
        },
        [updateBreakdownMutation, breakdownData]
    );

    const saveGlobal = useCallback(
        (value) => {
            updateGlobalMutation.mutate({ content: value });
        },
        [updateGlobalMutation]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
    const debouncedSave = useCallback(
        debounce(() => {
            const pending = pendingSaveRef.current;
            if (!pending) return;

            const { planId, section, value } = pending;

            if (section === 'global') {
                saveGlobal(value);
            } else if (planId && section) {
                saveBreakdown(planId, section, value);
            }

            pendingSaveRef.current = null;
            isDirtyRef.current = false;
        }, 2500),
        [saveBreakdown, saveGlobal]
    );

    // --- Flush pending save immediately (sync) ---
    const flushPendingSave = useCallback(() => {
        // Cancel the debounced call
        debouncedSave.cancel();

        // Execute save immediately if there's pending data
        const pending = pendingSaveRef.current;
        if (pending && isDirtyRef.current) {
            const { planId, section, value } = pending;

            if (section === 'global') {
                saveGlobal(value);
            } else if (planId && section) {
                saveBreakdown(planId, section, value);
            }

            pendingSaveRef.current = null;
            isDirtyRef.current = false;
        }
    }, [debouncedSave, saveBreakdown, saveGlobal]);

    // --- Handle content change from editor ---
    const handleContentChange = useCallback(
        (newContent) => {
            setContent(newContent);
            isDirtyRef.current = true;

            // Store pending save data
            pendingSaveRef.current = {
                planId: contentSourceRef.current.planId,
                section: contentSourceRef.current.section,
                value: newContent
            };

            // Trigger debounced save
            debouncedSave();
        },
        [debouncedSave]
    );

    // --- Load content when section/plan changes ---
    useEffect(() => {
        // Flush any pending save from previous section BEFORE loading new content
        flushPendingSave();

        // Determine new content based on selection
        let newContent = '';

        if (selectedSection === 'global') {
            newContent = globalData?.content || '';
            contentSourceRef.current = { planId: null, section: 'global' };
        } else if (selectedPlanId && selectedSection && breakdownData) {
            const [mainSection, subSection] = selectedSection.split('.');
            newContent = breakdownData[mainSection]?.[subSection] || '';
            contentSourceRef.current = { planId: selectedPlanId, section: selectedSection };
        } else {
            contentSourceRef.current = { planId: null, section: null };
        }

        setContent(newContent);
        isDirtyRef.current = false;
        pendingSaveRef.current = null;
    }, [selectedPlanId, selectedSection, breakdownData, globalData, flushPendingSave]);

    // --- Cleanup on unmount ---
    useEffect(
        () => () => {
            debouncedSave.cancel();
            // Flush any pending save on unmount
            const pending = pendingSaveRef.current;
            if (pending && isDirtyRef.current) {
                const { planId, section, value } = pending;
                if (section === 'global') {
                    // Fire and forget
                    TRADEBOOK.updateGlobal({ content: value }).catch(() => {});
                } else if (planId && section && breakdownData) {
                    const [mainSection, subSection] = section.split('.');
                    if (mainSection && subSection) {
                        // Create full object with updated value
                        const fullObject = {
                            ...breakdownData,
                            [mainSection]: {
                                ...breakdownData[mainSection],
                                [subSection]: value
                            }
                        };

                        TRADEBOOK.updateBreakdown(planId, fullObject).catch(() => {});
                    }
                }
            }
        },
        [debouncedSave]
    );

    return {
        content,
        handleContentChange,
        flushPendingSave,
        isSaving: updateBreakdownMutation.isLoading || updateGlobalMutation.isLoading
    };
}
